import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useAppSettings } from '@/lib/appSettings';
import { cn } from '@/lib/cn';
import { formatElapsed } from '@/lib/formatElapsed';
import { UNSUPPORTED_FILE_TYPE_MESSAGE, acceptAttrFor, isAllowedName } from '@/lib/tabularFile';
import type { FileUpload } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Ban,
  CheckCircle2,
  CircleStop,
  File,
  FileSpreadsheet,
  FileText,
  Loader2,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 현재 컴포저에 이미 첨부된 ID. 다이얼로그 내 체크박스 초기 상태. */
  initialSelectedIds: string[];
  /** 동시 첨부 한도 (남은 슬롯 계산에 사용). */
  maxAttachments: number;
  /** 현재 채팅 세션 id. 지정되면 이 세션에 첨부된 파일만 목록에 표시. */
  sessionId?: string;
  /** 세션 기준 목록에 합쳐 보여줄, 아직 메시지에 전송되지 않은 대기 파일들. */
  pendingFiles?: FileUpload[];
  /** 다이얼로그에서 새로 업로드된 파일을 즉시 composer attachments 로 올리는 콜백. */
  onUploaded?: (file: FileUpload) => void;
  onConfirm: (files: FileUpload[]) => void;
}

const STATUS_LABEL: Record<FileUpload['status'], string> = {
  uploading: '업로드 중',
  // 'uploaded' 는 업로드가 끝난 휴지 상태(엑셀은 메시지 전송 시 분석) — 사용자에겐 '준비됨'으로 표시.
  uploaded: '준비됨',
  queued: '대기 중',
  processing: '처리 중',
  ready: '준비됨',
  failed: '실패',
  canceled: '중지됨',
};

/**
 * 진행 중(중지 가능 + 경과 타이머) status. 'uploaded' 는 업로드 완료 휴지 상태라 제외 —
 * 증가하는 타이머/중지 버튼 없이 '준비됨'으로 보인다.
 */
function isInProgress(status: FileUpload['status']): boolean {
  return status === 'uploading' || status === 'queued' || status === 'processing';
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(
    d.getMinutes(),
  ).padStart(2, '0')}`;
}

function StatusBadge({ status, error }: { status: FileUpload['status']; error: string | null }) {
  const cls =
    status === 'ready' || status === 'uploaded'
      ? 'text-emerald-600 dark:text-emerald-400'
      : status === 'failed' || status === 'canceled'
        ? 'text-destructive'
        : 'text-foreground-muted';
  const Icon =
    status === 'ready' || status === 'uploaded'
      ? CheckCircle2
      : status === 'failed'
        ? AlertCircle
        : status === 'canceled'
          ? Ban
          : status === 'processing' || status === 'uploading'
            ? Loader2
            : null;
  return (
    <span
      className={cn('inline-flex items-center gap-1 text-xs', cls)}
      title={status === 'failed' && error ? error : undefined}
    >
      {Icon ? (
        <Icon
          className={cn(
            'size-3',
            (status === 'processing' || status === 'uploading') && 'animate-spin',
          )}
        />
      ) : (
        <span className="size-1.5 rounded-full bg-current" />
      )}
      {STATUS_LABEL[status]}
    </span>
  );
}

export function AttachFilesDialog({
  open,
  onOpenChange,
  initialSelectedIds,
  maxAttachments,
  sessionId,
  pendingFiles,
  onUploaded,
  onConfirm,
}: Props) {
  const qc = useQueryClient();
  const appSettings = useAppSettings();
  const allowedExts = appSettings.data?.uploadAllowedExts;
  const drmActive = appSettings.data?.drmActive ?? false;
  const [selected, setSelected] = useState<Set<string>>(new Set(initialSelectedIds));
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 다이얼로그 열릴 때마다 외부 selection 동기화.
  useEffect(() => {
    if (open) {
      setSelected(new Set(initialSelectedIds));
      setUploadError(null);
    }
  }, [open, initialSelectedIds]);

  // Escape 로 닫기.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onOpenChange]);

  const filesQuery = useQuery({
    queryKey: ['files', sessionId ?? null],
    queryFn: () => api.fileList(sessionId),
    enabled: open,
    refetchInterval: open ? 5000 : false,
  });

  const uploadMut = useMutation({
    mutationFn: api.fileUpload,
    onSuccess: (uploaded) => {
      qc.invalidateQueries({ queryKey: ['files'] });
      setUploadError(null);
      // 업로드 직후 파일은 아직 어떤 메시지에도 attached 되지 않아 세션 스코프 서버 응답에
      // 포함되지 않는다. composer attachments 에 바로 승격해 병합 목록에 나타나도록 한다.
      onUploaded?.(uploaded);
    },
    onError: (e) => {
      setUploadError(e instanceof Error ? e.message : '업로드 실패');
    },
  });

  const deleteMut = useMutation({
    mutationFn: api.fileDelete,
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['files'] });
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    },
  });

  const cancelMut = useMutation({
    mutationFn: api.fileCancel,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['files'] });
    },
  });

  // 세션 스코프 응답 (메시지에 이미 첨부된 파일) + 아직 전송되지 않은 대기 파일 병합.
  // pendingFiles 는 composer 의 attachments 상태 — DB 에는 있지만 어떤 메시지에도 attach 되지 않아
  // 서버 응답에는 아직 포함되지 않는다. 최신 createdAt 순으로 정렬, id 중복 제거.
  // sessionId 가 없는 경우 (새 대화 인덱스 화면) 는 서버 파일 전체를 노출하지 않고
  // 대기 파일만 보인다 — 이전 대화 파일이 노출되지 않도록.
  const files = useMemo(() => {
    const server = sessionId ? (filesQuery.data?.files ?? []) : [];
    const merged = new Map<string, FileUpload>();
    for (const f of server) merged.set(f.id, f);
    for (const f of pendingFiles ?? []) merged.set(f.id, f);
    return Array.from(merged.values()).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [filesQuery.data, pendingFiles, sessionId]);
  const storageReady = filesQuery.data?.storageReady ?? true;

  // 진행 중 파일이 있으면 1초마다 re-render 해 경과 시간을 흐르게 한다(폴링은 5초라 덜 매끄러움 보완).
  const hasActiveFile = files.some((f) => isInProgress(f.status));
  useEffect(() => {
    if (!hasActiveFile) return;
    const t = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [hasActiveFile]);

  const selectedFiles = useMemo(() => files.filter((f) => selected.has(f.id)), [files, selected]);

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    // input[accept] 는 브라우저 UX hint — "모든 파일" 옵션으로 우회 가능하므로 여기서 한 번 더 검증.
    // 서버가 내려준 허용 확장자(DRM 반영) 기준. 미로딩 시 전체 첨부 형식으로 fallback.
    if (!isAllowedName(f.name, allowedExts)) {
      setUploadError(
        drmActive
          ? `문서보안 환경에서는 일부 형식만 업로드할 수 있습니다 (허용: ${(allowedExts ?? []).join('/')}).`
          : UNSUPPORTED_FILE_TYPE_MESSAGE,
      );
      return;
    }
    uploadMut.mutate(f);
  };

  const toggle = (f: FileUpload) => {
    // 'failed' 는 첨부 불가. 'uploaded'/'processing' 상태도 선택 허용 — 사용자가 바로 메시지 전송하면
    // 서버가 인제스트 완료까지 대기 후 LLM 에 넘긴다.
    if (f.status === 'failed') return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(f.id)) next.delete(f.id);
      else if (next.size < maxAttachments) next.add(f.id);
      return next;
    });
  };

  const onConfirmClick = () => {
    onConfirm(selectedFiles);
    onOpenChange(false);
  };

  const onDelete = (f: FileUpload) => {
    if (!window.confirm(`"${f.originalName}" 파일을 삭제할까요?`)) return;
    deleteMut.mutate(f.id);
  };

  const onCancel = (f: FileUpload) => {
    if (!window.confirm(`"${f.originalName}" 처리를 중지할까요?`)) return;
    cancelMut.mutate(f.id);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/50"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      {/* biome-ignore lint/a11y/useSemanticElements: native <dialog> 는 backdrop/portal 동작이 달라 커스텀 모달 유지 */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="파일 첨부"
        className="w-full max-w-xl max-h-[80vh] flex flex-col rounded-2xl border border-border bg-surface shadow-[var(--shadow)]"
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="text-base font-semibold">파일 첨부</h2>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="닫기"
            className="size-8 grid place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </header>

        <div className="px-5 py-4 border-b border-border">
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptAttrFor(allowedExts)}
            onChange={onPickFile}
            className="hidden"
          />
          <Button
            type="button"
            variant="secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadMut.isPending || !storageReady}
            className="gap-2"
          >
            {uploadMut.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Upload className="size-4" />
            )}
            {uploadMut.isPending ? '업로드 중…' : '파일 업로드 (엑셀·CSV·PDF·Word·PPT·한글·텍스트)'}
          </Button>
          {drmActive ? (
            <div className="mt-2 text-xs text-foreground-muted">
              문서보안(DRM) 환경 — 보안 적용 가능한 형식만 업로드할 수 있습니다
              {allowedExts && allowedExts.length > 0 ? ` (${allowedExts.join(', ')})` : ''}.
            </div>
          ) : null}
          {!storageReady ? (
            <div className="mt-2 text-xs text-foreground-muted">
              오브젝트 스토리지가 구성되지 않아 업로드가 비활성화되어 있습니다. 관리자에게 설정을
              요청해 주세요.
            </div>
          ) : null}
          {uploadError ? <div className="mt-2 text-xs text-destructive">{uploadError}</div> : null}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {filesQuery.isLoading ? (
            <div className="text-sm text-foreground-muted py-4">불러오는 중…</div>
          ) : files.length === 0 ? (
            <div className="text-sm text-foreground-muted py-8 text-center">
              아직 업로드한 파일이 없습니다.
            </div>
          ) : (
            <ul className="space-y-1.5">
              {files.map((f) => {
                const checked = selected.has(f.id);
                const disabled = f.status === 'failed' || f.status === 'canceled';
                const Icon =
                  f.kind === 'excel' ? FileSpreadsheet : f.kind === 'document' ? FileText : File;
                return (
                  <li
                    key={f.id}
                    className={cn(
                      'flex items-center gap-3 px-2.5 py-2 rounded-md border border-transparent',
                      'hover:bg-surface-muted/60',
                      checked && 'bg-surface-muted/80 border-border',
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => toggle(f)}
                      aria-label={`${f.originalName} 선택`}
                      className="size-4 shrink-0 accent-accent disabled:opacity-40"
                    />
                    <Icon className="size-4 text-foreground-muted shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate" title={f.originalName}>
                        {f.originalName}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-foreground-muted mt-0.5">
                        <span>{formatSize(f.sizeBytes)}</span>
                        <span>·</span>
                        <span>{formatDate(f.createdAt)}</span>
                        <span>·</span>
                        <StatusBadge status={f.status} error={f.error} />
                        {f.status === 'queued' && (f.queueAhead ?? 0) > 0 ? (
                          <>
                            <span>·</span>
                            <span>앞에 {f.queueAhead}건</span>
                          </>
                        ) : null}
                        {isInProgress(f.status) ? (
                          <>
                            <span>·</span>
                            <span title="경과 시간">
                              {formatElapsed(nowTick - new Date(f.createdAt).getTime())}
                            </span>
                          </>
                        ) : null}
                        {f.uploadMs != null ? (
                          <>
                            <span>·</span>
                            <span title="MinIO 업로드 소요 시간">
                              업로드 {formatElapsed(f.uploadMs)}
                            </span>
                          </>
                        ) : null}
                        {f.status === 'ready' && f.readyAt ? (
                          <>
                            <span>·</span>
                            <span title="엑셀 파싱 + DB 적재">
                              분석{' '}
                              {formatElapsed(
                                new Date(f.readyAt).getTime() - new Date(f.createdAt).getTime(),
                              )}
                            </span>
                          </>
                        ) : null}
                      </div>
                    </div>
                    {isInProgress(f.status) ? (
                      <button
                        type="button"
                        onClick={() => onCancel(f)}
                        aria-label={`${f.originalName} 처리 중지`}
                        title="처리 중지"
                        disabled={cancelMut.isPending}
                        className="size-7 grid place-items-center rounded-md text-foreground-muted hover:text-destructive hover:bg-surface-muted disabled:opacity-40"
                      >
                        <CircleStop className="size-3.5" />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => onDelete(f)}
                      aria-label={`${f.originalName} 삭제`}
                      disabled={deleteMut.isPending}
                      className="size-7 grid place-items-center rounded-md text-foreground-muted hover:text-destructive hover:bg-surface-muted disabled:opacity-40"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <footer className="flex items-center justify-between px-5 py-4 border-t border-border">
          <div className="text-xs text-foreground-muted">
            {selected.size > 0
              ? `${selected.size}/${maxAttachments} 선택됨`
              : `최대 ${maxAttachments}개까지 첨부 가능`}
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              취소
            </Button>
            <Button type="button" onClick={onConfirmClick} disabled={selected.size === 0}>
              {selected.size > 0 ? `${selected.size}개 첨부` : '첨부'}
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
}
